import { assign, createMachine } from 'xstate';

import { LogInWithGoogle } from './sso';

type GoogleSignInContext = {
  error: unknown | string | undefined;
  gAccessToken: string | undefined;
};

type GoogleSignInEvent =
  | {
      type: 'log in with google';
      gAccessToken: string;
    }
  | {
      type: 'notify google log in succeeded';
      token: string;
      ssoUserId: number;
      isExistingUser: string;
    }
  | {
      type: 'notify google log in failed';
      error: unknown;
    };

export type GoogleSignInState =
  | {
      value: 'idle';
      context: { error: undefined; gAccessToken: undefined };
    }
  | {
      value: 'logging in with google' | 'google log in succeeded';
      context: { error: undefined; gAccessToken: string };
    }
  | {
      value: 'google log in failed';
      context: { error: string; gAccessToken: string };
    };

export function createGoogleSignInMachine({
  logInWithGoogle,
  on,
}: {
  logInWithGoogle: (
    params: Omit<
      Parameters<LogInWithGoogle>[0],
      'baseUrl' | 'product' | 'useCustomRlifeSocialAuth'
    >,
  ) => ReturnType<LogInWithGoogle>;
  on: {
    loggedIn: (
      token: string,
      ssoUserId: number,
      isExistingUser: string,
    ) => void;
    tokenObtained: (token: string, tokenObtained: number) => void;
    error: (val: string) => void;
  };
}) {
  return createMachine<
    GoogleSignInContext,
    GoogleSignInEvent,
    GoogleSignInState
  >(
    {
      id: 'google-signin',
      predictableActionArguments: true,
      preserveActionOrder: true,
      context: {
        error: undefined,
        gAccessToken: undefined,
      },
      initial: 'idle',
      states: {
        idle: {
          on: {
            'log in with google': {
              target: 'logging in with google',
            },
          },
        },
        'logging in with google': {
          invoke: {
            src: 'log in with google',
          },
          on: {
            'notify google log in failed': {
              target: 'google log in failed',
            },
            'notify google log in succeeded': {
              target: 'google log in succeeded',
            },
          },
        },
        'google log in failed': {
          entry: ['update error'],
          exit: ['clear error'],
          on: {
            'log in with google': {
              target: 'logging in with google',
            },
          },
        },
        'google log in succeeded': {
          entry: ['notify of token obtained', 'notify of logged in'],
          type: 'final',
        },
      },
    },
    {
      services: {
        'log in with google': (context, event) => async (send) => {
          console.info(context);
          try {
            if (event.type !== 'log in with google') {
              throw new Error(`Unable to log in without Google AccessToken`);
            }
            if (!event.gAccessToken) {
              throw new Error(`Unable to log in with an Google Access Token`);
            }

            const { token, ssoUserId, isExistingUser } = await logInWithGoogle({
              accessToken: event.gAccessToken,
            });

            send({
              type: 'notify google log in succeeded',
              token,
              ssoUserId,
              isExistingUser,
            });
          } catch (error) {
            send({ type: 'notify google log in failed', error });
            console.error(
              'Functional error: Error while logging in with google',
              JSON.stringify(error),
            );
          }
        },
      },
      actions: {
        'clear error': assign((context) => ({ ...context, error: undefined })),

        'update error': (context, event) => {
          if (event.type === 'notify google log in failed') {
            on.error(event.error as string);
          }
        },
        'notify of logged in': (context, event) => {
          if (event.type === 'notify google log in succeeded') {
            on.loggedIn(event.token, event.ssoUserId, event.isExistingUser);
          }
        },
        'notify of token obtained': (context, event) => {
          if (event.type === 'notify google log in succeeded') {
            on.tokenObtained(event.token, event.ssoUserId);
          }
        },
      },
    },
  );
}
